import { drawing as d, geometry as g } from '@progress/kendo-drawing';
import { Box } from "./../dom/Box";
import { TextBlock, defaultTextOptions } from "./TextBlock";
import { TextLayout } from "./TextLayout";
import { CENTER, START, END, NEW_LINE_CHAR, SPACE, EMPTY, INLINE_PADDING, TRANSPARENT } from '../constants';
import { deepExtend } from '../utils/deep-extend';
import { defined } from '../utils';
import { getSpacing } from './utils';
const parseAlign = (align = '') => {
    const result = {
        justifyContent: CENTER,
        alignContent: CENTER
    };
    if (align.includes('left')) {
        result.justifyContent = START;
    }
    if (align.includes('right')) {
        result.justifyContent = END;
    }
    if (align.includes('top')) {
        result.alignContent = START;
    }
    if (align.includes('bottom')) {
        result.alignContent = END;
    }
    return result;
};
function getContentSpacing(shape, relativePadding) {
    const spacing = getSpacing(relativePadding || 0, 0);
    const left = shape.width * spacing.left;
    const top = shape.height * spacing.top;
    const right = shape.width * spacing.right;
    const bottom = shape.height * spacing.bottom;
    return { left, top, right, bottom };
}
/** @hidden */
export const textRect = (options) => {
    let shapeSize = options.shapeSize;
    const { padding, relativePadding } = options;
    const userPadding = getSpacing(padding || 0, 0);
    const curPadding = deepExtend({}, defaultTextOptions.padding, userPadding);
    if (!shapeSize) {
        const lines = (options.text || EMPTY).split(NEW_LINE_CHAR);
        const style = getFont(options);
        const measures = lines.map(line => d.util.measureText(line, style));
        const maxWidth = measures.reduce((max, measured) => {
            return Math.max(max, measured.width);
        }, 0);
        const totalHeight = measures.reduce((sum, measured) => {
            return sum + measured.height;
        }, 0) + (lines.length - 1) * (options.lineSpacing || 0);
        shapeSize = {
            width: maxWidth,
            height: totalHeight
        };
    }
    const userRelativePadding = getContentSpacing(shapeSize, relativePadding || 0);
    const curRelativePadding = deepExtend({}, defaultTextOptions.relativePadding, userRelativePadding);
    const box = new Box(0, 0, shapeSize.width, shapeSize.height);
    box.unpad(curRelativePadding);
    box.unpad(curPadding);
    return box.toRect();
};
/** @hidden */
export const getFont = (options) => {
    const font = {
        fontSize: defined(options.fontSize) ? options.fontSize : defaultTextOptions.fontSize,
        fontFamily: options.fontFamily || defaultTextOptions.fontFamily
    };
    if (options.fontWeight || defaultTextOptions.fontWeight) {
        font.fontWeight = options.fontWeight || defaultTextOptions.fontWeight;
    }
    if (options.fontStyle || defaultTextOptions.fontStyle) {
        font.fontStyle = options.fontStyle || defaultTextOptions.fontStyle;
    }
    return font;
};
/**
 * A text block that supports multi-line text with line wrapping and alignment.
 * Extends TextBlock and provides functionality for rendering text across multiple lines with layout support.
 */
export class MultiLineTextBlock extends TextBlock {
    constructor() {
        super(...arguments);
        /** Whether the text block can be aligned within its container. */
        this.alignable = false;
        /** @hidden */
        this.name = 'MultiLineTextBlock';
    }
    /**
     * Gets or sets the text content of the multi-line text block.
     * @param text The text content to set, or undefined to get current content
     * @returns The current text content when called as getter
     */
    content(text) {
        if (text !== undefined) {
            this.options.text = text;
            this._initText();
        }
        return this.options.text;
    }
    /** @hidden */
    _initText() {
        this.textElements.length = 0;
        this.alignable = false;
        const drawingElement = this.drawingElement = this.drawingElement || new d.Group();
        if (drawingElement.children.length) {
            drawingElement.clear();
        }
        const options = this.options;
        const rect = textRect(options);
        const layout = new TextLayout(rect, {
            ...parseAlign(options.align),
            lineSpacing: options.lineSpacing
        });
        this._textLines(rect ? rect.size.width : undefined).forEach((line) => {
            const textElement = new d.Text(line, new g.Point(0, 0), {
                font: options.font
            });
            this.textElements.push(textElement);
            layout.append(textElement);
            layout.appendBreak();
        });
        layout.reflow();
        const hasBackground = options.background !== undefined;
        const hasBorder = options.border !== undefined;
        if (hasBackground || hasBorder) {
            // Store the text-only size BEFORE adding background (for proper centering)
            const textBbox = layout.clippedBBox();
            if (textBbox) {
                // Save text-only dimensions for positioning
                this._textOnlyRect = textBbox.clone();
                let padding = options.textPadding !== undefined ? options.textPadding : INLINE_PADDING;
                if (typeof padding === 'number') {
                    padding = getSpacing(padding, 0);
                }
                else {
                    padding = { ...INLINE_PADDING, ...padding };
                }
                // Create background rectangle
                // Note: stroke is centered on the path, so half will be inside, half outside
                const bgRect = new d.Rect(new g.Rect([
                    textBbox.origin.x - padding.left,
                    textBbox.origin.y - padding.top
                ], [
                    textBbox.size.width + padding.left + padding.right,
                    textBbox.size.height + padding.top + padding.bottom
                ]), {
                    stroke: { color: TRANSPARENT, ...options.border },
                    fill: { color: options.background }
                });
                // Insert background rectangle before text
                drawingElement.insert(0, bgRect);
            }
        }
        drawingElement.append(layout);
        // Apply text color (not fill/stroke from options)
        if (options.color) {
            this.textElements.forEach((el) => {
                el.fill(options.color);
            });
        }
        else if (!hasBackground && !hasBorder) {
            // Only apply default fill/stroke if no background rectangle
            this._fill();
            this._stroke();
        }
    }
    /** @hidden */
    _textLines(maxWidth) {
        const { text = EMPTY } = this.options;
        if (maxWidth === undefined) {
            return text.split(NEW_LINE_CHAR);
        }
        const style = getFont(this.options);
        const measureText = d.util.measureText;
        const lines = [];
        // Split by newline characters first
        const textLines = text.split(NEW_LINE_CHAR);
        textLines.forEach((line) => {
            const words = line.split(SPACE);
            let currentLine = EMPTY;
            words.forEach((word) => {
                while (measureText(word, style).width > maxWidth) {
                    let part = EMPTY;
                    for (let i = 1; i <= word.length; i++) {
                        const testPart = word.substring(0, i);
                        const width = measureText(testPart, style).width;
                        if (width > maxWidth) {
                            break;
                        }
                        part = testPart;
                    }
                    if (!part) {
                        break;
                    }
                    if (currentLine) {
                        lines.push(currentLine);
                        currentLine = EMPTY;
                    }
                    lines.push(part);
                    word = word.substring(part.length);
                }
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const width = measureText(testLine, style).width;
                if (width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                }
                else {
                    currentLine = testLine;
                }
            });
            lines.push(currentLine || SPACE);
        });
        return lines;
    }
}
